// Next SEO
import { BreadcrumbJsonLd, DefaultSeo } from 'next-seo';
import Head from 'next/head';
import {
  REEBELO_CURRENCY,
  REEBELO_DOMAINS,
  REEBELO_STORE_TO_LOCALE,
} from '@lambda/reebelo';
import { useRouter } from 'next/router';
import { ImageT } from '@lambda/apis/src/catalog-v2/types';
import { useProductPageContext } from './hooks/useProductPageContext';
import { generateCategoryPageUrl } from '@/components/collections-v3/hooks/helpers';
import {
  generateProductMultiImage,
  generateCarouselImages,
} from './sections/main/left/image/helpers';
import { getShortenCountryName } from '@/lib/use-origin-country';
import {
  buildProductPageTitle,
  generateCategoryWarranty,
} from './hooks/helpers';
import {
  buildFaqPageJsonLd,
  buildProductFaqEntities,
  buildProductUrl,
  buildProductVariants,
  buildProductJsonLd,
  buildReturnPolicy,
  buildSchemaDescription,
  buildShippingDetails,
  serialiseJsonLd,
} from './productSchema';
import settings from '@/settings';
import { interpolateStoreTokens } from '@/lib/store-tokens';
import i18n from '@/settings/i18n';
import { SITE_NAME } from '@/lib/site-name';

const t = i18n.init('product');

/** An image list in schema.org form, falling back to the single image field. */
const urlsOf = (images?: ImageT[], image?: ImageT) => {
  if (images?.length) {
    return images.map((img) => img.url);
  }

  if (image) {
    return [image.url];
  }

  return [];
};

const { store } = settings;

const Metadata = () => {
  const router = useRouter();
  const isOrganicAdRedirect = !!router.asPath?.includes('utm_organicad');

  const {
    selectedSkuDetails: { freeReturnDays, condition },
    selectedSku,
    selectedOffer,
    productId,
    product,
    availableVariants,
    additionalInfo: { breadcrumb, categoryWarrantyInfo, reviews },
    isOutOfStock,
  } = useProductPageContext();

  const formattedCategoryWarranty = generateCategoryWarranty({
    condition,
    categoryWarrantyInfo,
  });

  const reviewItems =
    (reviews?.reviews?.length ?? 0) > 0
      ? reviews?.reviews.map((r) => ({
          author: r.customerName,
          datePublished: r.createdAt,
          reviewBody: r.text,
          name: r.title,
          reviewRating: {
            bestRating: 5,
            ratingValue: r?.stars ?? 4.5,
            worstRating: 1,
          },
        }))
      : undefined;

  const description = t`Get the best price for ${{
    productName: product.name,
  }} in ${{
    country: t(getShortenCountryName()),
  }}. Shop a wide collection at up to 70% off retail. Free shipping, ${{
    warranty: formattedCategoryWarranty,
  }} warranty, and ${{ freeReturnDays }} day returns included.`;

  const title = buildProductPageTitle({
    productName: product.name,
    condition,
    breadcrumb,
    country: t(getShortenCountryName()),
  });

  const forceNewCondition = isOrganicAdRedirect;

  // Handling and transit times come from the selected offer's own standard
  // estimate rather than a store-wide guess.
  const standardEstimate = selectedOffer?.deliveryEstimates?.find(
    (estimate) => estimate.type === 'standard',
  );

  // The images the listing actually shows, for the selected variant.
  const carouselImages =
    generateCarouselImages(selectedSku, product, isOutOfStock) ?? [];

  // The multi-image the listing actually shows, for the selected variant.
  const shareImage = generateProductMultiImage(selectedSku, product) ?? '';

  const faqPageJsonLd = buildFaqPageJsonLd(
    buildProductFaqEntities(product.faq, product.name),
  );

  const productUrl = buildProductUrl(REEBELO_DOMAINS[store], productId);

  // Each variant node advertises its own photography. One shared array would
  // have every colour showing the selected SKU's images, so the product's own
  // gallery is the neutral fallback where a SKU carries none.
  const productImages = urlsOf(product.images, product.image);

  const imagesBySku = new Map(
    (product.skus ?? []).map((sku) => [sku.id, urlsOf(sku.images, sku.image)]),
  );

  const schemaDescription = buildSchemaDescription({
    description: interpolateStoreTokens(product.description ?? ''),
    fallback: description,
    productName: product.name,
  });

  const hasVariant = buildProductVariants({
    variants: availableVariants ?? [],
    productName: product.name,
    description: schemaDescription,
    brand: product.brand?.name,
    imagesForSku: (skuId) => {
      const own = imagesBySku.get(skuId);

      return own?.length ? own : productImages;
    },
    priceCurrency: REEBELO_CURRENCY[store],
    productUrl,
    forceNewCondition,
    isOutOfStock,
    // Merchant Center needs these to grant the "free delivery" and returns
    // badges; without them the offer is valid but earns no rich snippet.
    shippingDetails: buildShippingDetails({
      priceCurrency: REEBELO_CURRENCY[store],
      addressCountry: settings.country_code,
      handlingTime: standardEstimate?.handlingTime,
      transitTime:
        standardEstimate?.deliveryMinDays != null &&
        standardEstimate?.deliveryMaxDays != null
          ? {
              min: standardEstimate.deliveryMinDays,
              max: standardEstimate.deliveryMaxDays,
            }
          : undefined,
    }),
    hasMerchantReturnPolicy: buildReturnPolicy({
      addressCountry: settings.country_code,
      returnDays: freeReturnDays,
    }),
  });

  const productJsonLd = buildProductJsonLd({
    productName: product.name,
    description: schemaDescription,
    images: carouselImages.map((img) => img.url),
    brand: product.brand?.name,
    manufacturerName: product.brand?.name,
    reviews: reviewItems,
    aggregateRating: {
      ratingValue: reviews?.summary?.stars ?? 4.5,
      reviewCount: reviews?.summary?.count ?? 1,
    },
    productGroupID: product.psku ?? productId,
    url: productUrl,
    hasVariant,
  });

  const streamlinedBreadcrumb = breadcrumb
    ? [
        { name: 'Home', link: `https://${REEBELO_DOMAINS[store]}` },
        ...breadcrumb.map((c) => {
          const link = `https://${
            REEBELO_DOMAINS[store]
          }${generateCategoryPageUrl(c.name, c.pk)}`;

          return { name: c.name, link };
        }),
        { name: product.name, link: productUrl },
      ].map((c, i) => ({
        name: c.name,
        item: c.link,
        // schema.org ListItem positions start at 1.
        position: i + 1,
      }))
    : [];

  return (
    <>
      {/* Default SEO */}
      <DefaultSeo
        norobots
        // 🔹 Basic Page Info
        title={title}
        defaultTitle={`Reebelo ${getShortenCountryName()}`}
        description={description}
        // 🔹 Open Graph
        openGraph={{
          type: 'product',
          locale: REEBELO_STORE_TO_LOCALE[settings.store].replace('-', '_'), // Open Graph uses underscore for locale
          url: productUrl,
          siteName: SITE_NAME,
          title,
          description,
          images: [
            {
              url: shareImage,
              width: 1200,
              height: 630,
              alt: product.name,
              type: 'image/jpeg',
            },
          ],
        }}
        // 🔹 Twitter Card — X reads its own tags first and only falls back to
        // Open Graph, so a card without them can render untitled or imageless.
        twitter={{ cardType: 'summary_large_image' }}
        // 🔹 Additional Tags
        additionalMetaTags={[
          { name: 'publisher', content: 'Reebelo' },
          { name: 'twitter:title', content: title },
          { name: 'twitter:description', content: description },
          ...(shareImage
            ? [
                { name: 'twitter:image', content: shareImage },
                { name: 'twitter:image:alt', content: product.name },
              ]
            : []),
        ]}
      />
      {/* FAQ JSON-LD. Google retired FAQ rich results in May 2026, so this is
      here to be read by AI crawlers rather than to win a SERP treatment. */}
      {faqPageJsonLd && (
        <Head>
          <script
            key="jsonld-FAQPage"
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: serialiseJsonLd(faqPageJsonLd) }}
          />
        </Head>
      )}
      {/* Breadcrumb JSON-LD is structured data that shows a page’s position within your site’s hierarchy 
      (e.g., Home > Phones > iPhone 13). It helps search engines display breadcrumb navigation 
      in search results instead of long URLs. */}
      {streamlinedBreadcrumb.length > 0 && (
        <BreadcrumbJsonLd itemListElements={streamlinedBreadcrumb} />
      )}
      {/* Product JSON-LD is structured data that helps search engines understand a product’s 
      details (price, brand, rating, etc.). It enables rich search results with 
      enhanced listings showing reviews, price, and availability. */}
      <Head>
        <script
          key="jsonld-Product"
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: serialiseJsonLd(productJsonLd) }}
        />
      </Head>
    </>
  );
};

export default Metadata;
